import type { FC, Dispatch, SetStateAction } from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  Flex,
  Text,
  DropdownMenu,
  Button,
  Icon,
  Badge,
  Separator,
} from '@scale/scaleui-radix';
import { useGetCohorts, useGetCohortMappingsByConsumer } from '../../../../communication/apiClient';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { CohortSubscriptionType } from '@scale/cohorts';

interface CohortSelectorBaseProps {
  onMappingsChange?: Dispatch<SetStateAction<Map<string, CohortSubscriptionType>>>;
  disabled?: boolean;
  isReadOnly?: boolean;
  allowExactSubscription?: boolean;
}

interface SingleSelectProps extends CohortSelectorBaseProps {
  multiple?: false;
  value?: string;
  onValueChange?: (value: string) => void;
  consumerId?: string;
  consumerType?: string;
}

interface MultiSelectProps extends CohortSelectorBaseProps {
  multiple: true;
  value?: string[];
  onValueChange?: (value: string[]) => void;
  consumerId?: string;
  consumerType?: string;
}

type CohortSelectorProps = SingleSelectProps | MultiSelectProps;

export interface CohortOption {
  cohortId: string;
  cohortVersionId: string;
  cohortName: string;
  subscriptionType?: CohortSubscriptionType;
  versionNumber?: number;
}

export const CohortSelector: FC<CohortSelectorProps> = props => {
  const {
    onMappingsChange,
    value,
    onValueChange,
    consumerId,
    consumerType,
    disabled = false,
    isReadOnly = false,
    multiple = false,
    allowExactSubscription = true,
  } = props;
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebouncedValue(searchValue, 500);
  const [internalValue, setInternalValue] = useState<string | string[]>(multiple ? [] : '');
  const [selectedOptions, setSelectedOptions] = useState<Map<string, CohortOption>>(new Map());

  // Fetch cohort mappings by consumer if consumerId is provided
  const { data: cohortMappingsData } = useGetCohortMappingsByConsumer(
    consumerId ?? '',
    consumerType ?? '',
    { enabled: !!(consumerId && consumerType) },
  );

  // Determine which version IDs to fetch
  const versionIdsToFetch = useMemo(() => {
    if (!value) return undefined;
    return multiple ? (value as string[]) : [value as string];
  }, [value, multiple]);

  // Fetch cohorts by version IDs as fallback (when no consumerId or no mappings found)
  const shouldFetchByVersionIds =
    !consumerId || (cohortMappingsData !== undefined && cohortMappingsData.mappings?.length === 0);
  const { data: cohortsByVersionData } = useGetCohorts(
    {
      cohortVersionIds: shouldFetchByVersionIds ? versionIdsToFetch : undefined,
    },
    { enabled: shouldFetchByVersionIds && !!versionIdsToFetch && versionIdsToFetch.length > 0 },
  );

  // Build selectedOptions from fetched data
  useEffect(() => {
    if (value === undefined) return; // Only in controlled mode

    const newSelectedOptions = new Map<string, CohortOption>();

    if (consumerId && consumerType && cohortMappingsData?.mappings) {
      // Use cohort mappings data
      const valueArray = multiple ? (value as string[]) : value ? [value as string] : [];

      valueArray.forEach(cohortVersionId => {
        const mapping = cohortMappingsData.mappings.find(
          m => m.cohortVersionId === cohortVersionId,
        );
        if (mapping) {
          newSelectedOptions.set(cohortVersionId, {
            cohortId: mapping.cohortId,
            cohortVersionId: mapping.cohortVersionId,
            cohortName: mapping.cohortName,
            subscriptionType: mapping.subscriptionType as CohortSubscriptionType,
            versionNumber: mapping.versionNumber,
          });
        }
      });
    } else if (cohortsByVersionData?.data) {
      // Use cohorts fetched by version IDs
      const valueArray = multiple ? (value as string[]) : value ? [value as string] : [];

      valueArray.forEach(cohortVersionId => {
        const cohort = cohortsByVersionData.data.find(
          (c: any) => c.latestVersionId?.toString() === cohortVersionId,
        );
        if (cohort) {
          newSelectedOptions.set(cohortVersionId, {
            cohortId: cohort._id,
            cohortVersionId: cohort.latestVersionId?.toString() ?? '',
            cohortName: cohort.name,
            subscriptionType: CohortSubscriptionType.Latest,
            versionNumber: cohort.latestVersionNumber,
          });
        }
      });
    }

    setSelectedOptions(newSelectedOptions);
  }, [value, multiple, consumerId, consumerType, cohortMappingsData, cohortsByVersionData]);

  const { data, refetch: refetchCohorts } = useGetCohorts(
    { search: debouncedSearchValue },
    { enabled: false },
  );

  useEffect(() => {
    if (debouncedSearchValue.trim() !== '') {
      refetchCohorts();
    }
  }, [debouncedSearchValue, refetchCohorts]);

  const options: CohortOption[] = useMemo(() => {
    if (!data) return [];
    return data.data.map((cohort: any) => ({
      cohortId: cohort._id,
      cohortVersionId: cohort.latestVersionId?.toString() ?? '',
      cohortName: cohort.name,
      subscriptionType: CohortSubscriptionType.Latest,
      versionNumber: cohort.latestVersionNumber,
    }));
  }, [data]);

  const handleValueChange = (newValue: string | string[]) => {
    if (multiple) {
      // Multi-select mode: newValue is string[]
      const newValueArray = Array.isArray(newValue) ? newValue : [newValue];
      const newSelectedOptions = new Map<string, CohortOption>();

      newValueArray.forEach(cohortVersionId => {
        const existingOption = selectedOptions.get(cohortVersionId);
        const option =
          existingOption || options.find((o: any) => o.cohortVersionId === cohortVersionId);

        if (option) {
          newSelectedOptions.set(cohortVersionId, option);
        }
      });

      // Batch all mapping changes into a single update
      onMappingsChange?.((mappings: Map<string, CohortSubscriptionType>) => {
        const newMappings = new Map(mappings);
        // Add new selections
        newSelectedOptions.forEach(option => {
          newMappings.set(
            option.cohortId,
            option.subscriptionType ?? CohortSubscriptionType.Latest,
          );
        });
        // Remove deselected cohorts
        selectedOptions.forEach((option, cohortVersionId) => {
          if (!newValueArray.includes(cohortVersionId)) {
            newMappings.delete(option.cohortId);
          }
        });
        return newMappings;
      });

      setSelectedOptions(newSelectedOptions);

      // Update controlled or internal state
      if (onValueChange) {
        (onValueChange as (value: string[]) => void)(newValueArray);
      } else {
        setInternalValue(newValueArray);
      }
    } else {
      // Single-select mode: newValue is string
      const newValueString = newValue as string;
      const existingOption = Array.from(selectedOptions.values())[0];
      const option =
        (existingOption?.cohortVersionId === newValueString ? existingOption : null) ||
        options.find((o: any) => o.cohortVersionId === newValueString);

      const newSelectedOptions = new Map<string, CohortOption>();

      if (option) {
        newSelectedOptions.set(option.cohortVersionId, option);
        onMappingsChange?.((mappings: Map<string, CohortSubscriptionType>) => {
          const newMappings = new Map(mappings);
          // Remove previous selection
          if (existingOption) {
            newMappings.delete(existingOption.cohortId);
          }
          newMappings.set(
            option.cohortId,
            option.subscriptionType ?? CohortSubscriptionType.Latest,
          );
          return newMappings;
        });
      } else if (existingOption) {
        // Deselecting
        onMappingsChange?.((mappings: Map<string, CohortSubscriptionType>) => {
          const newMappings = new Map(mappings);
          newMappings.delete(existingOption.cohortId);
          return newMappings;
        });
      }

      setSelectedOptions(newSelectedOptions);

      // Update controlled or internal state
      if (onValueChange) {
        (onValueChange as (value: string) => void)(newValueString);
      } else {
        setInternalValue(newValueString);
      }
    }
  };

  const handleSubscriptionChange = (type: string, cohortVersionId?: string) => {
    let selectedOption: CohortOption | undefined;
    if (multiple) {
      if (!cohortVersionId) {
        return;
      }
      selectedOption = selectedOptions.get(cohortVersionId);
    } else {
      selectedOption = Array.from(selectedOptions.values())[0];
    }
    if (!selectedOption) {
      return;
    }
    const cohortId = selectedOption.cohortId;
    onMappingsChange?.((mappings: Map<string, CohortSubscriptionType>) => {
      const newMappings = new Map(mappings);
      newMappings.set(cohortId, type as CohortSubscriptionType);
      return newMappings;
    });
    const updatedOption = { ...selectedOption, subscriptionType: type as CohortSubscriptionType };
    const newSelectedOptions = new Map(selectedOptions);
    newSelectedOptions.set(selectedOption.cohortVersionId, updatedOption);
    setSelectedOptions(newSelectedOptions);
  };

  const filteredOptions: CohortOption[] = useMemo(() => {
    // Filter out already selected cohorts by cohortVersionId
    const currentValue = value !== undefined ? value : internalValue;

    if (multiple) {
      const selectedIds = Array.isArray(currentValue) ? currentValue : [];
      return options.filter((opt: any) => !selectedIds.includes(opt.cohortVersionId));
    }
    const selectedId = typeof currentValue === 'string' ? currentValue : '';
    return options.filter((opt: any) => opt.cohortVersionId !== selectedId);
  }, [options, value, internalValue, multiple]);

  // Determine the current value: controlled > internal > empty
  const currentValue = useMemo(() => {
    // Controlled mode: use provided value
    if (value !== undefined) {
      return value;
    }

    // Uncontrolled mode: use internal state
    return internalValue;
  }, [value, internalValue]);

  const getCohortDisplayName = (cohort: CohortOption | undefined) => {
    if (!cohort) return '';
    if (cohort.versionNumber && cohort.subscriptionType !== CohortSubscriptionType.Latest) {
      return `${cohort.cohortName} - V${cohort.versionNumber}`;
    }
    return cohort.cohortName;
  };

  if (isReadOnly) {
    const selectedOptionsArray = Array.from(selectedOptions.values());
    return (
      <Flex direction="row" gap="2" width="100%" align="center" wrap="wrap">
        {selectedOptionsArray.length > 0 ? (
          selectedOptionsArray.map(option => (
            <Badge key={option.cohortVersionId}>{getCohortDisplayName(option)}</Badge>
          ))
        ) : (
          <Badge>{getCohortDisplayName(undefined)}</Badge>
        )}
      </Flex>
    );
  }

  return (
    <Flex direction="row" gap="2" width="100%" align="center">
      <Flex direction="column" width="100%">
        {/* Search Input */}
        <Autocomplete.Root
          multiple={multiple}
          value={currentValue}
          onValueChange={handleValueChange}
          search={searchValue}
          onSearchChange={setSearchValue}
          disabled={disabled}
          renderValue={(item: { displayName: string; value: string }) => {
            return (
              <Flex direction="row" gap="1" align="center">
                {item.displayName}
                <Separator orientation="vertical" />
                {allowExactSubscription && (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <Button variant="ghost">
                        <Icon icon={faGear} label="Add cohort" size="1" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                      <DropdownMenu.RadioGroup
                        value={selectedOptions.get(item.value)?.subscriptionType}
                        onValueChange={type => handleSubscriptionChange(type, item.value)}
                      >
                        <DropdownMenu.RadioItem value={CohortSubscriptionType.Latest}>
                          <Text>Always use latest version</Text>
                        </DropdownMenu.RadioItem>
                        <DropdownMenu.RadioItem value={CohortSubscriptionType.Exact}>
                          <Text>Don't subscribe to updates</Text>
                        </DropdownMenu.RadioItem>
                      </DropdownMenu.RadioGroup>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                )}
              </Flex>
            );
          }}
        >
          <Autocomplete.Content>
            <Autocomplete.TextField placeholder="Search cohorts..." onPaste={() => {}}>
              <Autocomplete.Values />
              <Autocomplete.Reset />
            </Autocomplete.TextField>
            <Autocomplete.Items>
              {/* Render selected items hidden so they're in itemsByValue for display name */}
              {Array.from(selectedOptions.values()).map(selectedOption => (
                <Autocomplete.Item
                  key={`selected-${selectedOption.cohortVersionId}`}
                  value={selectedOption.cohortVersionId}
                >
                  {getCohortDisplayName(selectedOption)}
                </Autocomplete.Item>
              ))}
              {/* Render filtered (unselected) options as visible selectable items */}
              {filteredOptions.map((option: CohortOption) => (
                <Autocomplete.Item
                  key={option.cohortVersionId}
                  value={option.cohortVersionId}
                  keywords={[option.cohortName, option.cohortId]}
                >
                  {option.cohortName}
                </Autocomplete.Item>
              ))}
            </Autocomplete.Items>
          </Autocomplete.Content>
        </Autocomplete.Root>
      </Flex>
    </Flex>
  );
};
